import { Box } from '@mui/material';
import {
  AutoComplete,
  ReadOnly,
  useEntityPermission,
  useDataProvider,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { useEffect, useState, forwardRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

const ProductLine = forwardRef(
  ({ overwriteProps = {}, field, onChange, ...props }, ref) => {
    const nameKey = 'productLineName';
    const valueKey = 'id';

    const provider =
      process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
    const { get, getApiUrl } = useDataProvider(provider);
    const [defaultOptions, setDefaultOptions] = useState([]);

    const { setValue, control, clearErrors } = useFormContext();

    const currentValue = useWatch({
      control,
      name: field?.name || 'undefined',
    });

    const industry = useWatch({
      control,
      name: field?.dependencies?.industry || 'undefined',
    });

    const getProductLine = async ({ industryId, searchValue }) => {
      const searchParamm = searchValue ? `&productLineName=${searchValue}` : '';
      const result = await get(
        `${getApiUrl()}/sxcl/v1/industries/${industryId}/product-lines/?limit=10${searchParamm}`,
      );
      if (result.ok) {
        const productLineResult =
          result?.data?.data?.map(item => ({
            label: item[nameKey],
            value: item[valueKey],
          })) || [];
        return productLineResult;
      }
      return null;
    };

    const loadOptions = async (searchValue, callback) => {
      if (!searchValue) return [];
      const resultsData = await getProductLine({
        industryId: industry?.value,
        searchValue,
      });
      if (resultsData) {
        return callback(resultsData);
      }
      return callback([]);
    };

    useEffect(() => {
      if (field?.dependencies?.industry && !industry?.value) {
        setDefaultOptions([]);
        setValue(field.name, { label: '', value: '' });
      }
      clearErrors(props?.name);
    }, [industry?.value]);

    const { isHidden, isReadOnly } = useEntityPermission();

    if (isReadOnly(props?.permission, currentValue)) {
      return (
        <ReadOnly name={props?.name}>
          {currentValue?.label || currentValue?.[nameKey]}
        </ReadOnly>
      );
    }

    if (isHidden(props?.permission)) {
      return (
        <input
          type="hidden"
          name={props?.name}
          value={currentValue?.value || ''}
        />
      );
    }

    return (
      <Box>
        <AutoComplete
          {...(field?.dependencies?.industry &&
            !industry?.value && { disabled: true })}
          ref={ref}
          defaultOptions={defaultOptions}
          loadOptions={loadOptions}
          {...props}
          overwriteProps={overwriteProps}
          isClearable
          {...(field?.dependencies?.industry &&
            !industry?.value && {
              helperText: (
                <FormattedMessage
                  id="PLEASE_SELECT_INDUSTRY_TO_ENABLE_THIS_FIELD"
                  defaultMessage="Please select industry to enable this field"
                />
              ),
            })}
        />
      </Box>
    );
  },
);

export default observer(ProductLine);
